import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spin, message, Modal } from 'antd';
import {
  getEacProjectList,
  getEacReportSummary,
  restoreEacProject,
  type EacProjectItem,
} from '../../api';
import EacReportTable from './components/EacReportTable';
import PageHeader from './components/PageHeader';
import AppHeader from '../../components/AppHeader';
import ProjectInfoBar from './components/ProjectInfoBar';
import TeamManagementDrawer from './components/TeamManagementDrawer';
import type { EacDisplayMode } from './types';

const EacManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [displayMode, setDisplayMode] = useState<EacDisplayMode>('hours');
  const [teamDrawerOpen, setTeamDrawerOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // 项目列表：按任务要求仅取首条作为当前项目。
  const {
    data: projectList = [],
    isLoading: projectLoading,
    error: projectError,
  } = useQuery({
    queryKey: ['eac-project-list'],
    queryFn: () => getEacProjectList(),
  });

  const currentProject: EacProjectItem | undefined = useMemo(() => {
    return projectList[0];
  }, [projectList]);

  // 汇总列表：依赖当前项目 code。
  const {
    data: reportRows = [],
    isLoading: reportLoading,
    error: reportError,
    refetch: refetchReport,
  } = useQuery({
    queryKey: ['eac-summary-report', currentProject?.code],
    queryFn: () => getEacReportSummary(currentProject!.code),
    enabled: Boolean(currentProject?.code),
  });

  useEffect(() => {
    if (projectError instanceof Error) {
      messageApi.error(projectError.message);
    }
  }, [messageApi, projectError]);

  useEffect(() => {
    if (reportError instanceof Error) {
      messageApi.error(reportError.message);
    }
  }, [messageApi, reportError]);

  const handleRestoreProject = () => {
    if (!currentProject?.code || restoring) return;
    Modal.confirm({
      title: '确认恢复到最原始状态？',
      content: '初始化后新增的人员和应用的纠偏方案会彻底还原。',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        setRestoring(true);
        try {
          await restoreEacProject(currentProject.code);
          messageApi.success('项目数据已恢复到最原始状态');
          window.location.reload();
        } catch (err) {
          messageApi.error((err as Error).message || '恢复失败，请稍后重试');
        } finally {
          setRestoring(false);
        }
      },
    });
  };

  const showLoading = projectLoading || reportLoading;

  return (
    <div className="min-h-screen ">
      <AppHeader />
      {contextHolder}
      <PageHeader />

      <div className="mx-auto w-full">
        <ProjectInfoBar
          project={currentProject}
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onOpenTeamManagement={() => setTeamDrawerOpen(prev => !prev)}
          onRestoreProject={handleRestoreProject}
        />

        <main className="px-6 py-4">
          {showLoading ? (
            <div className="flex h-[320px] items-center justify-center rounded border border-[#d1d5db] bg-white">
              <Spin size="large" />
            </div>
          ) : (
            <EacReportTable rows={reportRows} displayMode={displayMode} />
          )}
        </main>
      </div>

      <TeamManagementDrawer
        open={teamDrawerOpen}
        project={currentProject}
        onClose={() => setTeamDrawerOpen(false)}
        onSaved={async () => {
          queryClient.invalidateQueries({ queryKey: ['eac-project-list'] });
          await refetchReport();
        }}
      />
    </div>
  );
};

export default EacManagementPage;
